import type { PaymentOverview } from '@vy/api-definition';
import type { CustomFieldValue } from '@vy/custom-fields-types';
import { findCustomFieldValue, getCustomFieldDisplayName, hasEntityPrefix } from '@vy/custom-fields-display';
import { isEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { vyDate, formatNumber } from '@vy/utils';
import Papa from 'papaparse';

import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import { formatDate } from '../../../../utils/date';
import { getPaymentProviderNameToDisplay } from '../../../invoices/InvoicesPage/invoices-page-components/MarkAsPaidDialog/constants/paymentProviderMap';
import { PAYMENTS_COLUMNS_KEYS } from '../payment-columns';

type CSVFieldOrder = {
  field: string;
  header: string;
  getValue: (payment: PaymentOverview) => string;
};

const CSV_FIELD_ORDER: CSVFieldOrder[] = [
  {
    field: PAYMENTS_COLUMNS_KEYS.CUSTOMER_NAME,
    header: 'Customer',
    getValue: (p) => p.customerName,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.AMOUNT,
    header: 'Amount',
    getValue: (p) => formatNumber(p.amount),
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.INVOICE_NUMBER,
    header: 'INV Number',
    getValue: (p) => p.invoiceNumber ?? '-',
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.CURRENCY,
    header: 'Currency',
    getValue: (p) => p.currency,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.STATUS,
    header: 'Status',
    getValue: (p) => p.status,
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.CREATION_TIME,
    header: 'Transaction Date',
    getValue: (p) =>
      isNotNullOrUndefined(p.externalPayment?.chargeTime)
        ? formatDate(vyDate(p.externalPayment.chargeTime), 'MM-DD-YYYY')
        : '-',
  },
  {
    field: PAYMENTS_COLUMNS_KEYS.PAYMENT_PROVIDER,
    header: 'Provider',
    getValue: (p) => {
      const paymentMethod = isNotNullOrUndefined(p.paymentMethod) ? p.paymentMethod : '---';
      return getPaymentProviderNameToDisplay(paymentMethod);
    },
  },
  {
    field: 'invoiceId',
    header: 'Invoice ID',
    getValue: (p) => p.invoiceId ?? '-',
  },
];

export function generateCSV(payments: PaymentOverview[], visibleFields?: string[]) {
  const baseFields = new Set(CSV_FIELD_ORDER.map((item) => item.field));

  const customFieldNamesFromData = extractUniqueCustomFieldNames(
    payments.map((payment) => payment.customFieldValues),
  );

  const customFieldNamesFromVisible =
    isNotNullOrUndefined(visibleFields) && !isEmptyArray(visibleFields)
      ? extractCustomFieldNamesFromVisible(visibleFields, baseFields)
      : [];

  const customFieldNames = [
    ...new Set([
      ...customFieldNamesFromData,
      ...customFieldNamesFromVisible
    ]),
  ].sort();

  const fieldOrder = [
    ...CSV_FIELD_ORDER,
    ...buildCustomFieldOrder(customFieldNames)
  ];

  const fieldsToExport = [
    ...getFieldsToExport(visibleFields),
    ...customFieldNames
  ];

  const selected = fieldOrder.filter((item) => fieldsToExport.includes(item.field));
  const headers = selected.map((item) => item.header);
  const rows = payments.map((payment) => selected.map((item) => item.getValue(payment)));

  return Papa.unparse([headers, ...rows]);
}

function getFieldsToExport(visibleFields?: string[]): string[] {
  if (!isNotNullOrUndefined(visibleFields)) {
    return CSV_FIELD_ORDER.map((item) => item.field);
  }

  return CSV_FIELD_ORDER.filter((item) => visibleFields.includes(item.field)).map((item) => item.field);
}

function extractCustomFieldNamesFromVisible(visibleFields: string[], baseFields: Set<string>): string[] {
  return visibleFields.filter((field) => !baseFields.has(field) && hasEntityPrefix(field));
}

function buildCustomFieldOrder(fieldNames: string[]): CSVFieldOrder[] {
  return fieldNames.map((fieldName) => ({
    field: fieldName,
    header: getCustomFieldDisplayName(fieldName),
    getValue: (payment) => getPaymentCustomFieldValue(payment.customFieldValues, fieldName),
  }));
}

function getPaymentCustomFieldValue(
  customFieldValues: CustomFieldValue[] | null | undefined,
  fieldName: string,
): string {
  const value = findCustomFieldValue(customFieldValues, fieldName);
  if (isNullOrUndefined(value)) {
    return '-';
  }

  return String(value);
}

